import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCourse } from "../../../../context/CourseContext";
import { useUtilityContext } from "../../../../context/UtilityContext";
import {
  Plus,
  X,
  ChevronDown,
  Calendar,
  FileText,
  Trash2,
  AlertCircle,
} from "lucide-react";
import SaveButton from "../../../../utils/CourseSaveButton";
import { useParams } from "react-router-dom";
import {
  card,
  cardTitle,
  microLabel,
  bodySecondary,
  accentBarSection,
  dividerInner,
  emptyState,
  motionFadeUp,
  motionStaggerChild,
  iconSize,
  pageTitle,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";

const WeeklyPlanner = () => {
  const { courseData, addWeek, updateWeek, removeWeek } = useCourse();
  const [newTopic, setNewTopic] = useState("");
  const [addingTopicWeek, setAddingTopicWeek] = useState(null);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const { courseID: urlCourseID } = useParams();
  const { selectedCourseId } = useUtilityContext();
  const courseID = urlCourseID || selectedCourseId;

  const handleAddTopic = (weekNumber) => {
    if (newTopic.trim()) {
      const week = courseData.weeklyPlan.find(
        (w) => w.weekNumber === weekNumber
      );
      const updatedTopics = [...week.topics, newTopic.trim()];
      updateWeek(weekNumber, updatedTopics);
      setNewTopic("");
      setAddingTopicWeek(null);
    }
  };

  const handleRemoveTopic = (weekNumber, topicIndex) => {
    const week = courseData.weeklyPlan.find((w) => w.weekNumber === weekNumber);
    const updatedTopics = week.topics.filter((_, index) => index !== topicIndex);
    updateWeek(weekNumber, updatedTopics);
  };

  const weeks = courseData?.weeklyPlan || [];
  const totalTopics = weeks.reduce(
    (sum, w) => sum + (w.topics?.length || 0),
    0
  );

  return (
    <motion.div {...motionFadeUp} className="mx-auto space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={pageTitle}>Weekly Plan</h1>
          <p className={cn(bodySecondary, "mt-1")}>
            {weeks.length} week{weeks.length !== 1 ? "s" : ""} &middot;{" "}
            {totalTopics} topic{totalTopics !== 1 ? "s" : ""}
          </p>
          <div className={cn(accentBarSection, "mt-3")} />
        </div>
        <SaveButton urlId={courseID} />
      </div>

      {/* Weeks — timeline layout */}
      {weeks.length > 0 ? (
        <div className="relative">
          {/* Timeline rail */}
          <div className="absolute left-[19px] top-6 bottom-6 w-px bg-gray-200 dark:bg-gray-700 hidden sm:block" />

          <div className="space-y-3">
            {weeks.map((week, weekIndex) => {
              const isExpanded = expandedWeek === week.weekNumber;
              const topicCount = week?.topics?.length || 0;

              return (
                <motion.div
                  key={week.weekNumber}
                  {...motionStaggerChild(weekIndex)}
                  className="relative"
                >
                  {/* Week card */}
                  <div className={cn(card, "overflow-hidden sm:ml-10")}>
                    {/* Timeline dot */}
                    <div className="absolute left-[12px] top-5 w-[15px] h-[15px] rounded-sm bg-accent1 border-2 border-white dark:border-gray-900 z-10 hidden sm:block" />

                    {/* Week header */}
                    <button
                      className={cn(
                        "w-full text-left px-4 sm:px-5 py-4 flex items-center gap-3 transition-colors",
                        isExpanded
                          ? "bg-accent1/5 dark:bg-accent1/10"
                          : "hover:bg-gray-50 dark:hover:bg-gray-800"
                      )}
                      onClick={() =>
                        setExpandedWeek(isExpanded ? null : week.weekNumber)
                      }
                    >
                      {/* Week number badge */}
                      <div className="flex-shrink-0 w-9 h-9 rounded-sm bg-accent1/10 dark:bg-accent1/20 flex items-center justify-center">
                        <span className="text-sm font-black text-accent1">
                          {week.weekNumber}
                        </span>
                      </div>

                      {/* Title */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white truncate">
                          Week {week.weekNumber}
                        </h3>
                        <p className={cn(bodySecondary, "text-[11px] mt-0.5")}>
                          {topicCount} topic{topicCount !== 1 ? "s" : ""}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span
                          role="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeWeek(week.weekNumber);
                          }}
                          className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-sm text-red-500 dark:text-red-400 transition-colors cursor-pointer"
                        >
                          <Trash2 className={iconSize.sm} />
                        </span>
                        <div
                          className={cn(
                            "p-1.5 rounded-sm transition-transform duration-200",
                            isExpanded && "rotate-180"
                          )}
                        >
                          <ChevronDown
                            className={cn(
                              iconSize.sm,
                              "text-gray-400 dark:text-gray-500"
                            )}
                          />
                        </div>
                      </div>
                    </button>

                    {/* Expanded topics */}
                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div
                            className={cn(
                              "border-t px-4 sm:px-5 py-4",
                              dividerInner
                            )}
                          >
                            {topicCount > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {week.topics.map((topic, topicIndex) => (
                                  <div
                                    key={topicIndex}
                                    className="group relative inline-flex items-center gap-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-sm px-3 py-1.5 hover:border-accent1/30 dark:hover:border-accent1/40 transition-colors"
                                  >
                                    <FileText className="h-3 w-3 text-accent1/50 flex-shrink-0" />
                                    <span className="text-sm text-gray-700 dark:text-gray-200">
                                      {topic}
                                    </span>

                                    {/* Hover delete */}
                                    <button
                                      onClick={() =>
                                        handleRemoveTopic(
                                          week.weekNumber,
                                          topicIndex
                                        )
                                      }
                                      className="hidden group-hover:flex p-0.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-sm text-red-400 transition-colors ml-1"
                                    >
                                      <X className="h-2.5 w-2.5" />
                                    </button>
                                  </div>
                                ))}

                                {/* Add topic inline button */}
                                {addingTopicWeek === week.weekNumber ? (
                                  <div className="inline-flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={newTopic}
                                      onChange={(e) =>
                                        setNewTopic(e.target.value)
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter")
                                          handleAddTopic(week.weekNumber);
                                        if (e.key === "Escape") {
                                          setAddingTopicWeek(null);
                                          setNewTopic("");
                                        }
                                      }}
                                      placeholder="Topic name"
                                      className="px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-sm text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-accent1 transition-colors min-w-[140px]"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() =>
                                        handleAddTopic(week.weekNumber)
                                      }
                                      className="px-3 py-1.5 bg-accent1 text-white rounded-sm text-sm font-bold hover:bg-accent1/90 transition-colors"
                                    >
                                      Add
                                    </button>
                                    <button
                                      onClick={() => {
                                        setAddingTopicWeek(null);
                                        setNewTopic("");
                                      }}
                                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm text-gray-400 transition-colors"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() =>
                                      setAddingTopicWeek(week.weekNumber)
                                    }
                                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border-2 border-dashed border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:border-accent1 hover:text-accent1 transition-colors text-sm"
                                  >
                                    <Plus className="h-3 w-3" />
                                    Add topic
                                  </button>
                                )}
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {addingTopicWeek === week.weekNumber ? (
                                  <div className="inline-flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={newTopic}
                                      onChange={(e) =>
                                        setNewTopic(e.target.value)
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter")
                                          handleAddTopic(week.weekNumber);
                                        if (e.key === "Escape") {
                                          setAddingTopicWeek(null);
                                          setNewTopic("");
                                        }
                                      }}
                                      placeholder="Topic name"
                                      className="px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-sm text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-accent1 transition-colors min-w-[140px]"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() =>
                                        handleAddTopic(week.weekNumber)
                                      }
                                      className="px-3 py-1.5 bg-accent1 text-white rounded-sm text-sm font-bold hover:bg-accent1/90 transition-colors"
                                    >
                                      Add
                                    </button>
                                    <button
                                      onClick={() => {
                                        setAddingTopicWeek(null);
                                        setNewTopic("");
                                      }}
                                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm text-gray-400 transition-colors"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-3">
                                    <p className={cn(bodySecondary, "text-sm")}>
                                      No topics yet.
                                    </p>
                                    <button
                                      onClick={() =>
                                        setAddingTopicWeek(week.weekNumber)
                                      }
                                      className="inline-flex items-center gap-1 text-xs font-bold text-accent1 hover:text-accent1/80 uppercase tracking-wider transition-colors"
                                    >
                                      <Plus className="h-3 w-3" />
                                      Add first topic
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className={emptyState}>
          <Calendar
            className={cn(
              iconSize.empty,
              "text-gray-300 dark:text-gray-600 mx-auto mb-3"
            )}
          />
          <h3 className={cn(cardTitle, "mb-1")}>No Weeks Created Yet</h3>
          <p className={cn(bodySecondary, "mb-0")}>
            Start building your weekly delivery plan by adding weeks and topics.
          </p>
        </div>
      )}

      {/* Add Week button */}
      <button
        onClick={addWeek}
        className="flex items-center justify-center w-full bg-accent1 text-white px-6 py-3 rounded-sm hover:bg-accent1/90 transition-colors font-bold uppercase tracking-wider text-xs shadow-sm hover:shadow-md"
      >
        <Plus className={cn(iconSize.md, "mr-2")} />
        Add New Week
      </button>

      {/* Info tip panel */}
      <div className="flex items-start gap-4 p-5 rounded-sm bg-accent1/5 dark:bg-accent1/10 border border-accent1/20 dark:border-accent1/30">
        <AlertCircle
          className={cn(
            iconSize.lg,
            "text-accent1 mt-0.5 flex-shrink-0"
          )}
        />
        <div>
          <p className="text-sm font-bold text-accent1 mb-1">Week Definition</p>
          <p className={bodySecondary}>
            Here "Week Plan" refers to a plan that covers the entire week,
            starting from Monday and ending on Sunday.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default WeeklyPlanner;
